import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  CountryCode,
} from 'libphonenumber-js';
import { SelectProps } from '@amzn/awsui-components-react';

const DEFAULT_COUNTRY: CountryCode = 'US';
const SENTINEL_PATTERN = /^0+$/;

/** Detects legacy sentinel phones where the entire string is digits and all zeros (e.g., 0000000000, 000-000-0000) */
function isLegacySentinelPhone(phone: string): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  return SENTINEL_PATTERN.test(digits);
}

export function buildCountryCodeOptions(): SelectProps.Options {
  return getCountries()
    .map((country) => ({
      value: country,
      label: `${country} (+${getCountryCallingCode(country)})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function parsePhoneWithCountry(phone: string): {
  countryCode: CountryCode;
  nationalNumber: string;
} {
  if (!phone || isLegacySentinelPhone(phone)) {
    return { countryCode: DEFAULT_COUNTRY, nationalNumber: '' };
  }
  if (!phone.startsWith('+')) {
    return { countryCode: DEFAULT_COUNTRY, nationalNumber: phone };
  }
  try {
    const parsed = parsePhoneNumber(phone);
    const national = parsed.nationalNumber;
    if (SENTINEL_PATTERN.test(national)) {
      return { countryCode: parsed.country ?? DEFAULT_COUNTRY, nationalNumber: '' };
    }
    if (parsed.country) {
      return { countryCode: parsed.country, nationalNumber: national };
    }
  } catch {
    // Fall through to default
  }
  const digits = phone.replace(/\D/g, '');
  if (SENTINEL_PATTERN.test(digits)) {
    return { countryCode: DEFAULT_COUNTRY, nationalNumber: '' };
  }
  return { countryCode: DEFAULT_COUNTRY, nationalNumber: digits };
}

export function formatPhoneForStorage(countryCode: CountryCode, nationalNumber: string): string {
  if (!nationalNumber) {
    const callingCode = getCountryCallingCode(countryCode);
    return `+${callingCode}0000000`;
  }
  const callingCode = getCountryCallingCode(countryCode);
  return `+${callingCode}${nationalNumber}`;
}

export function formatPhoneForDisplay(phone: string): string {
  if (!phone || isLegacySentinelPhone(phone)) return '';
  if (!phone.startsWith('+')) {
    // Legacy US-only: strip non-digits before formatting
    const digits = phone.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  try {
    const parsed = parsePhoneNumber(phone);
    if (SENTINEL_PATTERN.test(parsed.nationalNumber)) return '';
    return parsed.formatInternational();
  } catch {
    return phone;
  }
}
